/**
 * Bundles: a short chain of tools run in one go, with the file carried from each step to the next.
 * A bundle run costs one daily use however many steps it has (the run page logs it once).
 *
 * Every step except the last must hand its result on, which the tools do through the shared download
 * button. bundles-data.test.ts checks that for each bundle, that every tool id is real, and that ids are
 * unique. The sitemap script reads the `id:` lines below, so only a bundle carries an `id:` at the start
 * of a line.
 */
export interface BundleStep { toolId: string; note: string }

export interface Bundle {
  id: string;
  title: string;
  description: string;
  /** Who it is for, in one sentence. */
  audience: string;
  /** Why these steps in this order, and what goes wrong otherwise. Specific to this bundle. */
  why: string;
  /** What the person has at the end. */
  outcome: string;
  proTip: string;
  /** What the input should be. */
  startWith: string;
  matchPhrases: string[];
  steps: BundleStep[];
}

export const BUNDLES: Bundle[] = [
  {
    id: "podcast-cleanup",
    title: "Podcast Cleanup",
    description: "Take a raw voice recording to a clean, evenly loud, smaller file ready to publish.",
    audience: "Podcasters, interviewers and anyone who records speech and wants it to sound tidy.",
    startWith: "One audio file with speech (MP3, WAV, M4A and similar).",
    why: "Noise comes out first, because every later step would otherwise raise it along with the voice. Silences are trimmed next, so the loudness check is not skewed by long gaps. Loudness is evened out after that, and compression comes last because it is the only step that throws detail away; doing it earlier would make the other steps work on a poorer copy.",
    outcome: "A single audio file with steady volume, no long dead air and a size that is easy to upload.",
    proTip: "Listen to the result after the noise step before you continue. If the voice sounds thin, go back and use a lower strength; every later step builds on that file.",
    matchPhrases: ["clean up a podcast recording", "remove background noise and silence from audio", "make my voice recording sound better"],
    steps: [
      { toolId: "noise-remover", note: "Reduce background hiss and hum" },
      { toolId: "silence-remover", note: "Cut long pauses and dead air" },
      { toolId: "audio-normalizer", note: "Even out the volume to podcast level (peak limiter on)" },
      { toolId: "audio-compressor", note: "Save a smaller file for upload (this is file size, not dynamic compression)" },
    ],
  },
  {
    id: "audio-extract-publish",
    title: "Audio Extract and Publish",
    description: "Pull the sound out of a video, keep the part you want and save it in the format you need.",
    audience: "Anyone who needs the audio of a lecture, interview or clip as a separate file.",
    startWith: "One video file (MP4, MOV, WebM and similar).",
    why: "Extracting first means the later steps work on a small audio file instead of a large video. Trimming comes before converting so the conversion only encodes the part you keep, which is faster and avoids a second round of quality loss on parts you throw away.",
    outcome: "An audio file containing only the part you chose, in the format you picked.",
    proTip: "If you plan to publish as MP3, pick MP3 in the last step only; every earlier step keeps the original quality.",
    matchPhrases: ["extract audio from video and trim", "get the sound from a video as mp3", "save part of a video's audio"],
    steps: [
      { toolId: "extract-audio", note: "Take the sound track out of the video" },
      { toolId: "audio-trimmer", note: "Keep only the part you want" },
      { toolId: "audio-converter", note: "Save it as MP3, WAV, M4A or another format" },
    ],
  },
  {
    id: "video-edit-desk",
    title: "Video Edit Desk",
    description: "Cut a clip, turn it the right way up and shrink it so it can be sent or posted.",
    audience: "People sending phone videos by email or message, or posting them to a site with a size limit.",
    startWith: "One video file.",
    why: "Cutting first removes footage you do not need, so rotating and compressing handle less data and finish sooner. Compression goes last because it re-encodes the video, and doing it before the rotate step would mean encoding twice and losing detail twice.",
    outcome: "A shorter, upright video at a smaller file size.",
    proTip: "Video processing runs in your browser and a long clip takes a while on a phone. Trim as tightly as you can in the first step.",
    matchPhrases: ["trim rotate and compress a video", "make a phone video smaller and upright", "cut and shrink a video to send"],
    steps: [
      { toolId: "trim-video", note: "Cut to the part you need" },
      { toolId: "rotate-video", note: "Turn it the right way up" },
      { toolId: "compress-video", note: "Reduce the file size" },
    ],
  },
  {
    id: "print-ready-pdf",
    title: "Print-Ready PDF",
    description: "Remove the pages you do not need, trim the margins, number the pages and lock in any filled-in form fields.",
    audience: "Anyone printing a report, handout or document who wants it tidy and in order.",
    startWith: "One PDF.",
    why: "Pages come out first so the numbers that follow run 1, 2, 3 with no gaps. Cropping comes before numbering so the number sits inside the final page edge rather than in a margin that is cut away. Flattening is last because it turns filled-in form fields into plain page content, so what you checked is what prints.",
    outcome: "A PDF with only the pages you want, trimmed and numbered, with any filled-in form fields locked in so it prints the way it looks on screen.",
    proTip: "Check the cropped page in the preview before you move on. A crop that is slightly too tight is easy to redo at that step and awkward to fix later.",
    matchPhrases: ["prepare a pdf for printing", "remove pages and add page numbers to a pdf", "clean up a pdf before printing"],
    steps: [
      { toolId: "remove-pdf-pages", note: "Delete the pages you do not want" },
      { toolId: "crop-pdf", note: "Trim the margins" },
      { toolId: "add-page-numbers", note: "Number the remaining pages" },
      { toolId: "flatten-pdf", note: "Turn filled-in form fields into plain page content" },
    ],
  },
  {
    id: "secure-send-packet",
    title: "Secure Send Packet",
    description: "Black out private details, stamp the document and lock it with a password before you send it.",
    audience: "Anyone sharing a document that contains personal or confidential details.",
    startWith: "One PDF.",
    why: "Redaction has to be first: the watermark and password cover the page, but only redaction removes the hidden text underneath. The watermark goes on before the password because once a file is protected, no later step can change it.",
    outcome: "A password-protected PDF with the sensitive parts removed and a visible stamp such as CONFIDENTIAL.",
    proTip: "Send the password by a different route from the file, for example by message when the file goes by email.",
    matchPhrases: ["redact and password protect a pdf", "send a confidential pdf safely", "black out details and lock a pdf"],
    steps: [
      { toolId: "redact-pdf", note: "Remove names, numbers or any text you choose" },
      { toolId: "pdf-watermark", note: "Stamp it, for example CONFIDENTIAL" },
      { toolId: "pdf-password", note: "Lock it with a password" },
    ],
  },
  {
    id: "scan-to-searchable-pdf",
    title: "Scan to Searchable PDF",
    description: "Turn photos or scans of pages into one PDF whose text can be searched and copied, at a sensible size.",
    audience: "Anyone digitising paper: receipts, letters, forms and book pages.",
    startWith: "One or more photos or scans of pages (JPG, PNG and similar).",
    why: "The pages are joined into a PDF first because text recognition works on a PDF page by page. Recognition comes before compression, since reading text needs the sharpest version of the page; compressing first would blur small print and cost accuracy.",
    outcome: "One PDF with a real text layer you can search and select, smaller than the raw scans.",
    proTip: "Photograph pages flat, in even light and straight on. Recognition accuracy depends on the picture more than on any setting.",
    matchPhrases: ["make a scanned document searchable", "turn photos of pages into a searchable pdf", "scan paper to pdf with text"],
    steps: [
      { toolId: "image-to-pdf", note: "Join the page images into one PDF" },
      { toolId: "ocr-pdf", note: "Add a searchable text layer" },
      { toolId: "compress-pdf", note: "Reduce the file size" },
    ],
  },
  {
    id: "job-application-kit",
    title: "Job Application Kit",
    description: "Combine your CV, cover letter and certificates into one numbered PDF small enough to email.",
    audience: "Job applicants whose employer asks for a single file or has an upload size limit.",
    startWith: "Two or more PDFs, such as your CV, cover letter and certificates.",
    why: "Merging comes first so the page numbers run across the whole packet. Numbering is next, then compression last, since the file size limit applies to the finished packet and compression is the step that gets it under the limit.",
    outcome: "One PDF in the order you chose, numbered throughout and light enough to attach.",
    proTip: "Put the document the reader should see first at the top of the list in the merge step; the order you set there is the order of the packet.",
    matchPhrases: ["combine cv and cover letter into one pdf", "make a job application pdf small enough to email", "merge resume and certificates"],
    steps: [
      { toolId: "merge-pdf", note: "Combine CV, letter and certificates in order" },
      { toolId: "add-page-numbers", note: "Number the pages of the whole packet" },
      { toolId: "compress-pdf", note: "Get it under the upload limit" },
    ],
  },
  {
    id: "freelance-close-out",
    title: "Freelance Close-Out",
    description: "Put the invoice, timesheet and delivery notes into one stamped PDF for the client.",
    audience: "Freelancers and small agencies finishing a project and sending the final paperwork.",
    startWith: "Two or more PDFs, such as the final invoice, timesheet and a list of deliverables.",
    why: "Merging first gives the client one file instead of several that can get separated. The stamp, for example FINAL or PAID, goes on the combined file so it appears on every page, and compression is last so the packet is easy to email.",
    outcome: "One PDF with every page stamped and a size that fits in an email.",
    proTip: "Make the invoices with Invoice Generator or Quote Generator first and keep the PDFs together in one folder so they are easy to select at the merge step.",
    matchPhrases: ["send final invoice and timesheet as one pdf", "close out a freelance project paperwork", "combine invoice and deliverables into a pdf"],
    steps: [
      { toolId: "merge-pdf", note: "Combine invoice, timesheet and notes" },
      { toolId: "pdf-watermark", note: "Stamp every page, for example FINAL" },
      { toolId: "compress-pdf", note: "Make it easy to email" },
    ],
  },
];

export function getBundleById(id: string): Bundle | undefined {
  return BUNDLES.find((b) => b.id === id);
}

/** The /pipeline link that runs a bundle. */
export function bundleRunPath(id: string): string {
  return `/pipeline?bundle=${encodeURIComponent(id)}`;
}
